import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Music, ChevronRight, LogOut, GraduationCap, BookOpen, Lock, BellRing } from "lucide-react";
import { childApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATE_STYLE = {
  "Not Started": "bg-[#F3EFE0] text-[#566270]",
  Learning: "bg-[#F6EED8] text-[#8a6a10]",
  Practising: "bg-[#F6EED8] text-[#8a6a10]",
  Secure: "bg-[#EBF6F0] text-[#2D6A4F]",
  Mastered: "bg-[#FBF0F2] text-[#7A1C2E]",
};

export default function ModulePage() {
  const { moduleId } = useParams();
  const [data, setData] = useState(null);
  const [asked, setAsked] = useState({});
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    childApi.get("/child/modules").then((r) => setData(r.data));
  }, []); // mount-only: fetch the learner's module/topic tree

  const mod = data?.modules.find((m) => m.id === moduleId);
  const grades = useMemo(() => {
    const g = {};
    (mod?.topics || []).forEach((t) => {
      const gr = t.grade || 0;
      (g[gr] = g[gr] || []).push(t);
    });
    return g;
  }, [mod]);
  const gradeKeys = useMemo(() => Object.keys(grades).sort((a, b) => a - b), [grades]);

  if (!data) return <div className="min-h-screen bg-[#FAF7EE] flex items-center justify-center"><Music className="w-8 h-8 text-[#C69B3D] animate-pulse" /></div>;

  const isTheory = mod?.key === "theory";
  const levelWord = isTheory ? "Grade" : "Level";

  const start = async (payload) => {
    const { data: d } = await childApi.post("/child/challenge/start", payload);
    navigate("/play/session", { state: { sessionData: d } });
  };

  return (
    <div className="min-h-screen bg-[#FAF7EE]" data-testid="module-page">
      <header className="bg-[#0B192C] text-[#FAF7EE] px-5 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/play/home" className="flex items-center gap-2" data-testid="back-home-link">
            <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
            <span className="font-display font-bold">ChoirQuest</span>
          </Link>
          <button onClick={() => { logout(); navigate("/play"); }} data-testid="finish-session-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#7A1C2E] text-sm font-bold min-h-[48px]">
            <LogOut className="w-4 h-4" /> Finish Session
          </button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8">
        {!mod ? (
          <p className="text-[#566270]" data-testid="module-unavailable">This module isn't switched on for you at the moment — your choir staff can enable it.</p>
        ) : (
          <>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]" data-testid="module-title">{mod.name}</h1>
            <p className="text-sm text-[#566270] mt-1">{mod.description}</p>

            {gradeKeys.map((g) => (
              <section key={g} className="mt-7">
                {g !== "0" && (
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#0B192C]" data-testid={`grade-heading-${g}`}>
                      <GraduationCap className="w-5 h-5 text-[#C69B3D]" aria-hidden /> {levelWord} {g}
                    </h2>
                    {isTheory && (
                      <button onClick={() => start({ kind: "finale", grade: Number(g) })} data-testid={`grade-finale-${g}`}
                        className="px-4 py-2.5 rounded-xl bg-[#7A1C2E] text-white text-xs font-bold hover:bg-[#8F2438] transition-colors min-h-[44px]">
                        Grade {g} Finale
                      </button>
                    )}
                  </div>
                )}
                <div className="space-y-3">
                  {grades[g].map((t) => (
                    <div key={t.id} className="rounded-2xl bg-white border border-[#E5DEC9] p-4 sm:p-5" data-testid={`topic-row-${t.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-[#0B192C]">{t.name}</h3>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATE_STYLE[t.state]}`} data-testid={`topic-state-${t.id}`}>{t.state}</span>
                        {t.state !== "Not Started" && <span className="text-xs text-[#566270]">{t.accuracy}%</span>}
                      </div>
                      {t.question_count === 0 ? (
                        <p className="text-xs text-[#566270] mt-2">New content coming soon.</p>
                      ) : (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {t.has_learn && (
                            <button onClick={() => navigate(`/play/learn/${t.id}`)} data-testid={`learn-${t.id}`}
                              className="inline-flex items-center gap-1 px-4 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm hover:bg-[#12253E] transition-colors min-h-[48px]">
                              <BookOpen className="w-4 h-4" aria-hidden /> Learn
                            </button>
                          )}
                          {Array.from({ length: t.challenge_count }, (_, i) => {
                            const unlocked = !!t.unlocks?.[`c${i + 1}`];
                            return (
                              <button key={i} disabled={!unlocked}
                                onClick={() => unlocked && start({ topic_id: t.id, kind: "challenge", challenge_index: i })}
                                data-testid={`challenge-${t.id}-${i}`}
                                title={unlocked ? undefined : "Your choir leader will unlock this when you're ready"}
                                className={`inline-flex items-center gap-1 px-4 py-3 rounded-xl font-bold text-sm transition-colors min-h-[48px] ${
                                  unlocked
                                    ? "bg-[#C69B3D] text-[#0B192C] hover:bg-[#D8AB49]"
                                    : "bg-[#F3EFE0] text-[#566270]/60 cursor-not-allowed"}`}>
                                {!unlocked && <Lock className="w-4 h-4" aria-hidden />}
                                Challenge {i + 1} {unlocked && <ChevronRight className="w-4 h-4" aria-hidden />}
                              </button>
                            );
                          })}
                          {(() => {
                            const testOpen = !!t.unlocks?.test;
                            return (
                              <button disabled={!testOpen}
                                onClick={() => testOpen && start({ topic_id: t.id, kind: "test" })}
                                data-testid={`topic-test-${t.id}`}
                                title={testOpen ? undefined : "Your choir leader will unlock this when you're ready"}
                                className={`px-4 py-3 rounded-xl border-2 font-bold text-sm transition-colors min-h-[48px] inline-flex items-center gap-1 ${
                                  testOpen
                                    ? "border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C] hover:text-[#FAF7EE]"
                                    : "border-[#E5DEC9] text-[#566270]/60 cursor-not-allowed"}`}>
                                {!testOpen && <Lock className="w-4 h-4" aria-hidden />}
                                Topic Test
                              </button>
                            );
                          })()}
                          {(() => {
                            const lockedItems = [];
                            for (let i = 0; i < t.challenge_count; i++) if (!t.unlocks?.[`c${i + 1}`]) lockedItems.push(`c${i + 1}`);
                            if (!t.unlocks?.test) lockedItems.push("test");
                            if (!lockedItems.length) return null;
                            const item = lockedItems[0];
                            const label = item === "test" ? "the Topic Test" : `Challenge ${item.slice(1)}`;
                            const key = `${t.id}:${item}`;
                            const serverAsked = (t.requested || []).includes(item) ? "Already requested — your choir director has it." : null;
                            if (asked[key] || serverAsked) {
                              return <span key={key} className="text-xs font-bold text-[#2D6A4F] py-3" data-testid={`unlock-asked-${t.id}`}>{asked[key] || serverAsked}</span>;
                            }
                            return (
                              <button key={key} data-testid={`ask-unlock-${t.id}`}
                                onClick={async () => {
                                  try {
                                    const r = await childApi.post("/child/unlock-request", { topic_id: t.id, item });
                                    setAsked((a) => ({ ...a, [key]: r.data.message || "Request sent to your choir director." }));
                                  } catch {
                                    setAsked((a) => ({ ...a, [key]: "Couldn't send right now — try again in a moment." }));
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border-2 border-dashed border-[#C69B3D] text-[#8a6a10] font-bold text-xs hover:bg-[#F6EED8] transition-colors min-h-[48px]">
                                <BellRing className="w-4 h-4" aria-hidden /> Ask your choir director to unlock {label}
                              </button>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
